// "Copy last charge" on one draft: the draft takes the lines (codes, modifiers,
// units, charges) and POS of the patient's most recent claim with the same rendering
// provider. Never diagnoses. One person's click on one draft, then they edit.
//
// It REPLACES the draft's existing lines, so the click opens a small confirm that
// says so; the copy itself is one request, disabled while in flight. Gates itself
// on `features.charge_templates` and a draft status, so each mount is one line:
//
//   <CopyLastChargeButton practiceId={practiceId} claim={claim} onCopied={reload} />

import React, { useRef, useState } from "react";
import useCapabilities from "../../hooks/useCapabilities";
import { copyLastCharge, lineLabel } from "./api";
import "./Charges.css";

export default function CopyLastChargeButton({
  practiceId,
  claim,
  readOnly = false,
  onCopied,
}) {
  const { features } = useCapabilities(practiceId);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // {kind: 'copied'|'error', ...}
  const inFlight = useRef(false);

  const status = claim ? (claim.claim_status ?? claim.claimStatus) : null;
  const claimId = claim ? (claim.id ?? claim.claimId) : null;
  if (!practiceId || claimId == null || readOnly) return null;
  if (!features.charge_templates) return null;
  // The server compares `(claim_status or "").strip().lower() != "draft"`, so a null
  // status is NOT a draft there even though the ledger paints it as one. Matching it
  // here keeps the button off a claim whose only outcome would be a 409.
  if (String(status || "").trim().toLowerCase() !== "draft") return null;

  const copy = async (e) => {
    if (e) e.stopPropagation();
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    try {
      const body = await copyLastCharge(practiceId, claimId);
      setResult({ kind: "copied", body });
      setConfirming(false);
      if (onCopied) onCopied(body);
    } catch (err) {
      setResult({ kind: "error", message: err.message });
      setConfirming(false);
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  return (
    <span className="chg-inline" onClick={(e) => e.stopPropagation()}>
      {!confirming ? (
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => {
            setResult(null);
            setConfirming(true);
          }}
          title="Take the lines of this patient's last claim with the same provider"
        >
          Copy last charge
        </button>
      ) : (
        <span className="chg-confirm" role="group" aria-label="Confirm copy">
          <span>Replace this draft's lines with the last charge?</span>
          <button
            className="btn btn-primary btn-sm"
            onClick={copy}
            disabled={busy}
          >
            {busy ? "Copying…" : "Replace lines"}
          </button>
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => setConfirming(false)}
            disabled={busy}
          >
            Cancel
          </button>
        </span>
      )}
      {result && result.kind === "copied" && (
        <span className="chg-note good" role="status">
          Copied from claim #{result.body.copiedFrom?.claimId} (
          {result.body.copiedFrom?.dateOfService || "—"}):{" "}
          {result.body.lines.map(lineLabel).join(", ") || "no lines"}. Diagnoses
          were not copied.
        </span>
      )}
      {result && result.kind === "error" && (
        <span className="chg-note bad" role="alert">
          {result.message}
        </span>
      )}
    </span>
  );
}
